import Layout from "../../components/layout";
import { useNavigate, useParams } from "react-router-dom";
import { styled } from "styled-components";
import { screenSizes } from "../../styles/theme";
import {
  ArrowBack,
  BlankImage,
  FilterIconOrange,
  FireIcon,
  LeafIcon,
} from "../../shared/icons";
import AISearchBar from "./AISearchInputBox";
import { useState, useEffect } from "react";
import { filterSearchNFTsAndCollections } from "../../slices/home/homeThunks";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { Grid2 as Grid, Skeleton, Box } from "@mui/material";
import Loader from "../../components/shared-components/Loader";
import { IconButton, IconButtonResponsive } from "../../components/ButtonsNew";
import FilterDrawer from "./FilterDrawer";
import { useSelector } from "react-redux";
import { getValidImageUrl } from "../../utils";

const Container = styled.div`
  padding: 2rem;
  margin-top: 5rem;
  @media (max-width: ${screenSizes.L}px) {
    padding: 1rem;
    margin-top: 3rem;
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  @media (max-width: ${screenSizes.L}px) {
    margin-top: 25px;
  }
  div {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  h1 {
    @media (max-width: ${screenSizes.L}px) {
      font-size: 20px;
    }
  }
`;

const Card = styled.div`
  padding: 12px;
  background-color: #fff;
  border: 1px solid rgba(9, 28, 0, 0.1);
  img {
    height: 224px;
    width: 100%;
    margin-bottom: 10px;
    background-size: cover;
    background-position: center;
    @media (max-width: ${screenSizes.L}px) {
      height: 107px;
      margin-bottom: 0px;
    }
  }
  .icon {
    margin-bottom: 5px;
  }
  h5 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: rgba(9, 28, 0, 1);
    margin-bottom: 5px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
  }
  h6 {
    margin: 0;
    font-family: "Manrope", sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: rgba(9, 28, 0, 1);
  }
`;

const NoResults = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
`;

const StyledCard = styled(Card)<{ isMobile: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? "row" : "column")};
  align-items: ${(props) => (props.isMobile ? "center" : "flex-start")};
  padding: ${(props) => (props.isMobile ? "10px" : "12px")};
  gap: ${(props) => (props.isMobile ? "2px" : "0px")};
  height: ${(props) => (props.isMobile ? "127px" : "342px")};
`;

const SkeletonWrapper = styled(Box)<{ isMobile: boolean }>`
  width:100%;
  display: flex;
  justify-content: space-between;
  align-self: center;
  @media (max-width: ${screenSizes.L}px) {
    width: 150px;
  }
`;

const ContentWrapper = styled(Box)<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: left;
  padding-left: ${(props) => (props.isMobile ? "8px" : "0px")};
  width: 100%;
  height: ${(props) => (props.isMobile ? "100%" : "auto")};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 25vh;
`;

const BackButtonWrapper = styled.div`
  top: 15%;
  display: flex;
  position: absolute;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  p {
    font-weight: 600;
    font-size: 14px;
  }
  svg {
    height: 20px;
    width: 20px;
  }
`;

const PriceAndScore = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  @media (max-width: ${screenSizes.L}px) {
    margin-top: 20px;
  }
`;

const Rating = styled.div<{ value: number }>`
  display: flex;
  align-items: center;
  background-color: ${({ value }) =>
    value <= 3 ? "#C7FFC5" : value >= 6 ? "#FFC5C5" : "#feffc5"};
  border: 1px solid rgba(9, 28, 0, 0.1);
  padding: 0 8px;
  border-radius: 40px;
  min-width: 70px;
  justify-content: center;
  span {
    font-size: 14px;
    font-weight: 400;
    color: #091c00;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 40px;
`;

const Title = styled.h5`
  margin: 8px 0;
  @media (max-width: ${screenSizes.L}px) {
    margin: 0;
  }
`;

const SearchResult = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const params = useParams();
  const { network, impactScore, categories, ecoCategories } = useSelector(
    (state: RootState) => state.searchFilter
  );
  const isMobile = window.innerWidth <= 1024;

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any>([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [searchStr, setSearchStr] = useState(params.query);

  const handleImageLoad = () => setImageLoaded(true);

  useEffect(() => {
    if (params?.query) {
      handleSearch(
        params.query || params?.query,
        100,
        1,
        network,
        impactScore,
        categories,
        ecoCategories
      );
    } else {
      handleSearch(
        "aaa",
        100,
        1,
        network,
        impactScore,
        categories,
        ecoCategories
      );
    }
  }, []);

  const handlePrice = (networkId: string|number, price: string|number) => {
    let calculatedPrice = (networkId === 1)
    ? Number(price).toFixed(6)
    : (networkId === 295)
    ? Number(price)
    : Number(price).toFixed(6);
    return calculatedPrice;
  }

  const handleSearch = async (
    query: any,
    limit: number = 100,
    page: number = 1,
    blockchain_network: string,
    rank_by: string,
    category_id: string,
    eco_category_id: string
  ) => {
    if(!query.length){
      return;
    }
    setSearchStr(query);
    setIsSearchLoading(true);
    try {
      const res: any = await dispatch(
        filterSearchNFTsAndCollections({
          keyword: query || params?.query,
          limit,
          page,
          blockchain_network,
          rank_by,
          category_id,
          eco_category_id,
        })
      );
      const combinedData = res?.payload?.data?.data?.combinedData?.map(
        (item: any) => ({
          id: item._id,
          chain:
            item?.networkId === 1
              ? "ethereum"
              : item?.networkId === 295
              ? "hedera"
              : "polygon",
          name: item?.name,
          imageUrl: item?.featureImage || item?.fileUrl,
          price: handlePrice(item?.networkId, item?.price ?? item?.floorPrice),
          score: item.score || 0,
          networkId: item?.networkId || 1,
          type: item?.assetType,
        })
      );
      setSearchResults(combinedData);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const renderCard = (item: any) => (
    <Grid key={item.id} size={{ xs: 12, md: 2.4 }}>
      <StyledCard
        isMobile={isMobile}
        onClick={() => navigate(`/${item.type}-details/${item.id}`)}
      >
        <SkeletonWrapper isMobile>
          {!imageLoaded && (
            <Skeleton
              variant="rectangular"
              width={isMobile ? 107 : 200}
              height={isMobile ? 107 : 200}
              animation="wave"
            />
          )}
          <img
            src={getValidImageUrl(item?.imageUrl, BlankImage)}
            alt={`${item.type}_image`}
            style={{
              display: imageLoaded ? "block" : "none",
              width: "100%",
              height: isMobile ? "107px" : "224px",
            }}
            onLoad={handleImageLoad}
          />
        </SkeletonWrapper>

        <ContentWrapper isMobile>
          <FireIcon className="icon" />
          <Title title={item.name}>
            {item.name}
          </Title>
          {/* <h6 style={{ margin: 0 }}>{item.ecoScore?.toFixed(2) || "0.00"} ETH</h6> */}
          <PriceAndScore>
            <h6
              style={{
                margin: 0,
                fontSize: "16px",
                fontWeight: 400,
                color: "#091C00",
              }}
            >
              {item?.price}{" "}
              {item?.chain == "ethereum"
                ? "ETH"
                : item?.chain == "hedera"
                ? "HBAR"
                : "POL"}
            </h6>
            <Rating value={item?.score}>
              <LeafIcon />
              <span>
                {typeof item?.score === "number" ? item.score.toFixed(1) : "0"}
              </span>
            </Rating>
          </PriceAndScore>
        </ContentWrapper>
      </StyledCard>
    </Grid>
  );

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const handleToggleFilter = () => {
    setIsFilterOpen((prev) => !prev);
  };

  return (
    <Layout fullScreen>
      <Container>
        <HeaderWrapper>
          <h1>Search Results</h1>
        </HeaderWrapper>
        {!isMobile && (
          <BackButtonWrapper onClick={() => navigate(-1)}>
            <ArrowBack />
            &nbsp;&nbsp;
            <p>Back</p>
          </BackButtonWrapper>
        )}

        {/* AI search component */}
        <AISearchBar
          isHeaderSearch
          isFilterSearch
          handleSearchWithFilter={handleSearch}
          isLoading={isSearchLoading}
          searchResults={searchResults}
          setSearchStr={setSearchStr}
        />

        <HeaderContainer>
          {!isMobile ? (
            <IconButton
              icon={<FilterIconOrange />}
              onClick={handleToggleFilter}
            >
              Filter
            </IconButton>
          ) : (
            <IconButtonResponsive
              icon={<FilterIconOrange />}
              onClick={handleToggleFilter}
            >
              {""}
            </IconButtonResponsive>
          )}
        </HeaderContainer>

        {/* filter drawer */}
        <FilterDrawer
          isOpen={isFilterOpen}
          onClose={handleToggleFilter}
          getFilteredNFTs={handleSearch}
          query={searchStr}
        />

        <Grid container spacing={2}>
          {isSearchLoading ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <>
              {searchResults?.length > 0 ?
                searchResults.map((item: any) => renderCard(item))
                :
                <LoaderWrapper>
                  <NoResults>No Results Found!</NoResults>
                </LoaderWrapper>
              }
            </>
          )}
        </Grid>
      </Container>
    </Layout>
  );
};

export default SearchResult;
